import { useEffect, useMemo, useState } from 'react'
import { NavLink, useNavigate } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../app/ReduxStore.ts'
import {
  useClubAdminCreateCheckoutSessionMutation,
  useCreateClubMutation,
  useUpdateUserMutation,
} from '../../features/SpeechAgendaApiSlice.ts'
import { setNeedsRegistration } from '../../features/FirebaseAuthSlice.ts'
import { CreateClubRequest } from '../../generated'
import AuthStep from '../../components/auth/AuthStep.tsx'
import SignInLoadingSpinner from '../../components/login/SignInLoadingSpinner.tsx'
import TopNav from '../../components/navigation/TopNav.tsx'
import { getDefaultTimeZone, getTimeZones } from '../../utils/TimezoneUtils.ts'
import { saveSignUpDraft } from '../../utils/SignUpDraft.ts'

type ProvisionError = 'ALREADY_REGISTERED' | 'GENERIC' | null

export default function RegisterClub() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const firebaseUser = useSelector(
    (state: RootState) => state.firebaseAuth.user
  )

  const timezones = useMemo(() => getTimeZones(), [])

  const [clubName, setClubName] = useState('')
  const [timezone, setTimezone] = useState(getDefaultTimeZone())
  const [toastmastersId, setToastmastersId] = useState('')

  const [clubNameError, setClubNameError] = useState<string | null>(null)
  const [timezoneError, setTimezoneError] = useState<string | null>(null)
  const [provisionError, setProvisionError] = useState<ProvisionError>(null)

  const [pendingProvision, setPendingProvision] = useState(false)
  const [provisioning, setProvisioning] = useState(false)

  const [updateUser] = useUpdateUserMutation()
  const [createClub] = useCreateClubMutation()
  const [createCheckoutSession] = useClubAdminCreateCheckoutSessionMutation()

  const validateForm = (): boolean => {
    let valid = true
    if (!clubName.trim()) {
      setClubNameError('Club name is required.')
      valid = false
    } else {
      setClubNameError(null)
    }
    if (!timezone) {
      setTimezoneError('Please choose a timezone.')
      valid = false
    } else {
      setTimezoneError(null)
    }
    return valid
  }

  const buildBody = (): CreateClubRequest => ({
    clubName: clubName.trim(),
    timezone,
    ...(toastmastersId.trim() ? { toastmastersId: toastmastersId.trim() } : {}),
  })

  // PUT /user (idempotent) must land before POST /clubs, otherwise the club
  // creation 404s on an unknown user.
  const runProvision = async () => {
    setProvisioning(true)
    setProvisionError(null)
    try {
      await updateUser().unwrap()
    } catch (err) {
      setProvisioning(false)
      if (
        err &&
        typeof err === 'object' &&
        'status' in err &&
        err.status === 422
      ) {
        // Backend user already exists, so getUserDetails is safe; clearing the
        // flag lets TopNav surface their account.
        dispatch(setNeedsRegistration(false))
        setProvisionError('ALREADY_REGISTERED')
      } else {
        // PUT /user did not create the backend user. Keep needsRegistration
        // true so TopNav's useAppAuth lookup stays skipped — otherwise a
        // getUserDetails 404 would sign the user out and redirect them away
        // before they can retry.
        setProvisionError('GENERIC')
      }
      return
    }

    let clubId: number
    try {
      const response = await createClub(buildBody()).unwrap()
      dispatch(setNeedsRegistration(false))
      clubId = response.clubId
    } catch {
      dispatch(setNeedsRegistration(false))
      setProvisioning(false)
      setProvisionError('GENERIC')
      return
    }

    try {
      const { url } = await createCheckoutSession(String(clubId)).unwrap()
      window.location.assign(url)
    } catch {
      navigate(`/club/${clubId}/admin`)
    }
  }

  // Synchronous auth methods (Google / email+password) only signal intent; we
  // wait for the Firebase token to land in the store so prepareHeaders can attach
  // it before provisioning runs.
  useEffect(() => {
    if (pendingProvision && firebaseUser && !provisioning) {
      setPendingProvision(false)
      void runProvision()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingProvision, firebaseUser, provisioning])

  const handleCreateClick = () => {
    if (!validateForm()) return
    setPendingProvision(true)
  }

  const handleBeforePasswordless = (email: string, displayName: string) => {
    if (!validateForm()) return false
    saveSignUpDraft({ email, displayName, club: buildBody() })
    return true
  }

  if (provisioning) {
    return (
      <>
        <TopNav />
        <SignInLoadingSpinner message="Creating your club, please wait..." />
      </>
    )
  }

  return (
    <>
      <TopNav />
      <div className="flex min-h-full flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img
            className="mx-auto h-10 w-auto"
            style={{ width: '60px', height: '60px' }}
            src="/logo.svg"
            alt="Speech Agenda"
          />
          <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
            Start your club
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Name your club and we'll make you its admin.
          </p>
          <p className="mt-2 text-center text-sm text-gray-600">
            90-day free trial, then $19/month per club. No card required to
            start - add one before the trial ends to keep access. Can cancel
            anytime
          </p>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="space-y-6">
            <div>
              <label
                htmlFor="clubName"
                className="block text-sm font-medium leading-6 text-gray-900">
                Club name
              </label>
              <input
                id="clubName"
                name="clubName"
                type="text"
                value={clubName}
                onChange={(e) => {
                  setClubName(e.target.value)
                  if (clubNameError) setClubNameError(null)
                }}
                className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
              {clubNameError && (
                <p className="mt-1 text-sm text-red-600">{clubNameError}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="timezone"
                className="block text-sm font-medium leading-6 text-gray-900">
                Timezone
              </label>
              <select
                id="timezone"
                name="timezone"
                value={timezone}
                onChange={(e) => {
                  setTimezone(e.target.value)
                  if (timezoneError) setTimezoneError(null)
                }}
                className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6">
                {timezones.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
              {timezoneError && (
                <p className="mt-1 text-sm text-red-600">{timezoneError}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="toastmastersId"
                className="block text-sm font-medium leading-6 text-gray-900">
                Toastmasters club number{' '}
                <span className="text-gray-400">(optional)</span>
              </label>
              <input
                id="toastmastersId"
                name="toastmastersId"
                type="text"
                value={toastmastersId}
                onChange={(e) => setToastmastersId(e.target.value)}
                className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
            </div>

            {provisionError === 'ALREADY_REGISTERED' && (
              <p className="text-sm text-red-600">
                This email already belongs to an account.{' '}
                <NavLink
                  to="/login"
                  className="font-semibold text-indigo-600 hover:text-indigo-500">
                  Please sign in
                </NavLink>
                .
              </p>
            )}
            {provisionError === 'GENERIC' && (
              <p className="text-sm text-red-600">
                Something went wrong while creating your club. Please try again.
              </p>
            )}

            {firebaseUser ? (
              <button
                type="button"
                onClick={handleCreateClick}
                className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                Create club
              </button>
            ) : (
              <div className="border-t border-gray-200 pt-6">
                <p className="mb-4 text-sm font-medium text-gray-900">
                  Create your account to finish
                </p>
                <AuthStep
                  onBeforeAuth={validateForm}
                  onAuthenticated={() => setPendingProvision(true)}
                  onBeforePasswordless={handleBeforePasswordless}
                  passwordlessSentMessage="Open it on this device to finish creating your club."
                />
              </div>
            )}
          </div>

          {!firebaseUser && (
            <div className="mt-8 flex items-center justify-center">
              <div className="text-sm leading-6">
                <span className="text-gray-600">Already have an account? </span>
                <NavLink
                  to="/login"
                  className="font-semibold text-indigo-600 hover:text-indigo-500">
                  Sign in
                </NavLink>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
